import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import { API_MANAGEMENT_ORDERS } from '@/constants/urls'
import { axiosApi } from '@/services/http/axiosApi'
import { STATUS_ERROR_PAGE } from '@/constants/urls'
import { ORDERS_FIELDS } from '@/constants/urls'
import { OrderItemsFilter, OrderFilter } from '@/constants/urls'
import { now, today, sixMonthsAgo } from '@/helpers/date-helpers'
import { type Order, type OrderItem } from '@/stores/orders.types'
import dayjs from 'dayjs'
import { bffClient } from '@accor/customer-core'
import { gql } from 'graphql-request'
import { getCurrency } from '@/helpers/currency'
import { isLuxuryBrand } from '@/constants/brands'

const MAX_NUMBER_OF_BOOKINGS_FROM_API = 100

const makeFilter = (filters: Record<string, string[]>) =>
  Object.entries(filters)
    .map((pairs) => `${pairs[0]}:${pairs[1].join('+')}`)
    .join(';')

export enum BookingStatusType {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED'
}
export enum BookingSortType {
  ASC = 'asc',
  DESC = 'desc'
}

export type BookingStatus = BookingStatusType.ACTIVE | BookingStatusType.CANCELLED
export type BookingSort = BookingSortType.ASC | BookingSortType.DESC
export interface BookingQueryParams {
  currency: string
  endDate: string
  fields: string
  filter: string
  getStays: boolean
  offset: number
  sortType: BookingSortType
  startDate: string
  startDepartureDate: string
  statusExclude?: BookingStatus
  statusInclude?: BookingStatus
}

const getBookings = async (
  staticParams: Partial<BookingQueryParams>,
  dynamicParams?: Partial<BookingQueryParams>
) => {
  try {
    const { data } = await axiosApi.get(API_MANAGEMENT_ORDERS, {
      params: {
        currency: getCurrency(),
        fields: String(ORDERS_FIELDS),
        ...staticParams,
        ...dynamicParams
      }
    })
    return data || []
  } catch (e) {
    console.error(e)
    window.location.href = STATUS_ERROR_PAGE
  }
}

const getUpcomingAndCurrentBookings = (pagination = {}): Promise<Array<Order>> =>
  getBookings(
    {
      sortType: BookingSortType.ASC,
      getStays: false,
      startDate: sixMonthsAgo,
      startDepartureDate: today,
      statusInclude: BookingStatusType.ACTIVE,
      filter: makeFilter({
        orderItems: [OrderItemsFilter.isHotelBooking, OrderItemsFilter.hasHotelName],
        $: [OrderFilter.isOrderItemsNonEmpty]
      })
    },
    pagination
  )

const getPastBookings = (pagination = {}): Promise<Array<Order>> =>
  getBookings(
    {
      getStays: true,
      filter: makeFilter({
        orderItems: [OrderItemsFilter.isHotelBooking, OrderItemsFilter.hasHotelName],
        $: [OrderFilter.isOrderItemsNonEmpty]
      })
    },
    pagination
  )

const getUpcomingCancelledBookings = (pagination = {}): Promise<Array<Order>> =>
  getBookings(
    {
      statusInclude: BookingStatusType.CANCELLED,
      startDate: today,
      filter: makeFilter({
        orderItems: [OrderItemsFilter.isHotelBooking, OrderItemsFilter.hasHotelName],
        $: [OrderFilter.isOrderItemsNonEmpty]
      })
    },
    pagination
  )

export interface ExperienceItem {
  id: string
  title: string
  url: string
  mainPictureUrl: string
  pricing: {
    startingPrice: number
    currency: string
    rewardPoints: number
  }
  rating: {
    rating: number
    outOf: number
  } | null
}

export interface ActivitiesData {
  booking: {
    hotel: {
      experiences: {
        paginationCursor: string
        items: Array<ExperienceItem>
      }
    }
  }
}

export type UpcomingCancelledAndPastResult = {
  value: Order[]
  hasNext: boolean
  newCount: number
}

export const useOrderStore = defineStore('orders', () => {
  const activeBookings: Ref<Array<Order>> = ref([])
  const pastBookings: Ref<Array<Order>> = ref([])
  const upcomingCancelledBookings: Ref<Array<Order>> = ref([])
  const feverActivities: Ref<Array<ExperienceItem>> = ref([])
  const hasApiAlreadyBeenCalled: Ref<boolean> = ref(false)

  const loadActiveUpcomingAndCurrentBookings = async ({
    paginate = true,
    forceRefresh = false
  }) => {
    try {
      if (!forceRefresh && hasApiAlreadyBeenCalled.value)
        return { value: activeBookings.value, hasNext: false }

      const bookings = await getUpcomingAndCurrentBookings({
        offset: paginate ? activeBookings.value.length : 0
      })
      activeBookings.value = paginate ? [...activeBookings.value, ...bookings] : bookings
      hasApiAlreadyBeenCalled.value = true
      return { value: activeBookings.value, hasNext: false }
    } catch (err) {
      console.error(err)
    }
  }

  const loadPastBookings = async ({
    startDate = '',
    endDate = '',
    endDepartureDate = '',
    offset = 0
  }: {
    startDate: string
    endDate: string
    endDepartureDate: string
    offset: number
  }): Promise<UpcomingCancelledAndPastResult | undefined> => {
    try {
      const bookings = await getPastBookings({
        startDate,
        endDate,
        endDepartureDate,
        offset
      })

      pastBookings.value = [...pastBookings.value, ...bookings]

      return {
        value: pastBookings.value,
        hasNext: bookings.length === MAX_NUMBER_OF_BOOKINGS_FROM_API,
        newCount: bookings.length
      }
    } catch (err) {
      console.error(err)
    }
  }

  const loadUpcomingCancelledBookings = async (
    paginate = true
  ): Promise<UpcomingCancelledAndPastResult | undefined> => {
    try {
      const bookings = await getUpcomingCancelledBookings({
        offset: paginate ? upcomingCancelledBookings.value.length : 0
      })
      upcomingCancelledBookings.value = paginate
        ? [...upcomingCancelledBookings.value, ...bookings]
        : bookings
      return { value: upcomingCancelledBookings.value, hasNext: false, newCount: bookings.length }
    } catch (err) {
      console.error(err)
    }
  }

  const getFeverActivities = async (
    bookingId: string,
    dateFrom?: string,
    dateTo?: string,
    limit = 3
  ) => {
    const queryBff = gql`
      query ActivitiesQuery($number: String!, $dateFrom: Date, $dateTo: Date, $limit: Int!) {
        booking(number: $number) {
          hotel {
            experiences(dateFrom: $dateFrom, dateTo: $dateTo, limit: $limit) {
              items {
                id
                title
                url
                mainPictureUrl
                pricing {
                  startingPrice
                  currency
                  rewardPoints
                }
              }
            }
          }
        }
      }
    `

    const resultQuery: ActivitiesData = await bffClient().request(queryBff, {
      number: bookingId,
      dateFrom,
      dateTo,
      limit
    })
    feverActivities.value = resultQuery?.booking?.hotel?.experiences?.items || []

    return feverActivities
  }

  const isCurrentBooking = (item: OrderItem): boolean => {
    if (!item.hotelBooking) return false
    return dayjs(item.hotelBooking.reservation.stayInfo.arrivalDate).isSameOrBefore(now())
  }

  const currentBookings = computed<Order[]>(() =>
    activeBookings.value.filter((item: Order) =>
      item.orderItems.some((order) => isCurrentBooking(order))
    )
  )

  const upcomingBookings = computed(() => {
    if (currentBookings.value.length > 0) {
      return activeBookings.value.filter(
        (booking) => !currentBookings.value.some((current) => current.id === booking.id)
      )
    }
    return activeBookings.value
  })

  const upcomingCancelledAndPastBookings = computed<Order[]>(() => {
    return [...upcomingCancelledBookings.value, ...pastBookings.value]
  })

  // This is for guest notifs in vue2
  const nextOrderId = computed(() => upcomingBookings.value[0]?.id)
  const nextOrderHotelBooking = computed(
    () => upcomingBookings.value[0]?.orderItems[0]?.hotelBooking
  )
  const nextOrderHotel = computed(() => nextOrderHotelBooking.value?.hotel || {})
  const nextOrderReservation = computed(() => nextOrderHotelBooking.value?.reservation || {})
  const nextOrderIsLuxuryBrand = computed(() => isLuxuryBrand(nextOrderHotel.value?.brand?.code))

  return {
    activeBookings,
    upcomingBookings,
    currentBookings,
    pastBookings,
    upcomingCancelledBookings,
    upcomingCancelledAndPastBookings,
    loadActiveUpcomingAndCurrentBookings,
    loadPastBookings,
    loadUpcomingCancelledBookings,
    feverActivities,
    getFeverActivities,
    nextOrderId,
    nextOrderHotel,
    nextOrderHotelBooking,
    nextOrderReservation,
    nextOrderIsLuxuryBrand
  }
})
